<template>
  <div>
    <div class="row" :class="{'disabled-form': creatingPaymentMethod}">
      <div class="col s12 m4">
        <label for="cardNumber">Card Number</label>
        <div id="cardNumber" ref="cardNumber" class="stripe-input" :class="{'disabled-input': creatingPaymentMethod}"></div>
      </div>

      <div class="col s4 m2">
        <label for="cardExpiry">Exp Date</label>
        <div id="cardExpiry" ref="cardExpiry" class="stripe-input" :class="{'disabled-input': creatingPaymentMethod}"></div>
      </div>

      <div class="col s4 m2">
        <label for="cardCvc">CVC</label>
        <div id="cardCvc" ref="cardCvc" class="stripe-input" :class="{'disabled-input': creatingPaymentMethod}"></div>
      </div>

      <div class="col s4 m2">
        <label for="addressZip">{{ zipLabel }}</label>
        <input id="addressZip" ref="addressZip" class="zip-input" :class="{'disabled-input': creatingPaymentMethod}" type="text" v-mask="zipMask" v-model="addressZip"
          :style="{ color: foregroundColor }" placeholder="90210" v-validate="'required|length:' + zipLength"
          @input="onZipChange" data-vv-name="postalCode" :disabled="creatingPaymentMethod" />
      </div>
    </div>

    <div v-if="creatingPaymentMethod" class="!verification-status center">
      <i class="fas fa-spinner fa-spin grey-text"></i>
      <span class="grey-text">Setting up payment method...</span>
    </div>

    <div id="card-errors" class="red-text text-darken-3">{{ cardError }}</div>
    <div class='red-text'>{{ errorMessage }}</div>
    <div class='red-text'>{{ processingErrorMessage }}</div>
  </div>
</template>
<script>
export default {
  props: {
    publishableKey: {
      type: String,
      required: true
    },
    qid: {
      type: String,
      required: true
    },
    presetAddressZip: {
      type: String,
      default: ''
    },
    foregroundColor: {
      type: String,
      default: '#32325d'
    },
  },
  mounted() {
    this.loadStripeJs()
    this.$bus.$on('regenerate-card-token', this.regenerateToken)
  },
  beforeDestroy() {
    this.$bus.$off('regenerate-card-token', this.regenerateToken)
  },
  data() {
    return {
      errorMessage: null,
      processingErrorMessage: null,
      cardError: null,
      cardNumberComplete: false,
      cardExpiryComplete: false,
      cardCvcComplete: false,
      postalCodeComplete: false,
      currentCardBrand: '',
      addressZip: this.presetAddressZip || "",
      creatingPaymentMethod: false,
      setupIntentClientSecret: null
    }
  },
  methods: {
    loadStripeJs() {
      this.script = document.createElement('script')
      this.script.setAttribute('src', 'https://js.stripe.com/v3/')
      document.head.appendChild(this.script)
      this.script.onload = this.setupStripe
    },

    async setupStripe() {
      this.stripe = Stripe(this.publishableKey);
      
      this.setupCardElements();
    },
    
    async createSetupIntent() {
      try {
        // Create Setup Intent via Public API
        const response = await this.$http.post("/api/v1/public/setup_intents", {
          publishable_key: this.publishableKey,
          qid: this.qid,
          usage: "off_session",
          payment_method_types: ["card"]
        });

        if (response.data.errors.length > 0) {
          this.processingErrorMessage = response.data.errors[0];
          return;
        }

        this.setupIntentClientSecret = response.data.client_secret;
        
      } catch (error) {
        if (error.status === 429) {
          this.processingErrorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (error.status === 400) {
          this.processingErrorMessage = 'Invalid request. Please refresh the page and try again.';
        } else if (error.body?.errors?.[0]) {
          this.processingErrorMessage = error.body.errors[0];
        } else {
          this.processingErrorMessage = 'Unable to process card. Please try again later.';
        }
        this.notifyError(this.processingErrorMessage);
      }
    },
    
    setupCardElements() {
      this.elements = this.stripe.elements({ locale: "en" });

      // Create individual elements for card number, expiry, CVC
      this.cardNumber = this.elements.create("cardNumber", {
        disableLink: true,
        style: {
          base: {
            color: this.foregroundColor,
            fontWeight: "500",
            fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
            fontSize: "14px",
            iconColor: "#c4f0ff",
            "::placeholder": { color: "#aab7c4" },
          },
          invalid: { color: "#fa755a", iconColor: "#fa755a" },
        },
      });

      this.cardExpiry = this.elements.create("cardExpiry", {
        style: {
          base: {
            color: this.foregroundColor,
            fontWeight: "500",
            fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
            fontSize: "14px",
            "::placeholder": { color: "#aab7c4" },
          },
          invalid: { color: "#fa755a" },
        },
      });

      this.cardCvc = this.elements.create("cardCvc", {
        style: {
          base: {
            color: this.foregroundColor,
            fontWeight: "500",
            fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
            fontSize: "14px",
            "::placeholder": { color: "#aab7c4" },
          },
          invalid: { color: "#fa755a" },
        },
      });
      
      // Mount each element to its respective container
      this.cardNumber.mount(this.$refs.cardNumber);
      this.cardExpiry.mount(this.$refs.cardExpiry);
      this.cardCvc.mount(this.$refs.cardCvc);

      // Listen for validation errors or changes
      this.cardNumber.on("change", this.onChange);
      this.cardExpiry.on("change", this.onChange);
      this.cardCvc.on("change", this.onChange);
    },
    
    onChange({ error, elementType, complete, brand }) {
      if (error) {
        this.cardError = error.message;
      } else {
        this.cardError = null;
        // Update completion status based on elementType
        if (elementType === 'cardNumber') {
          this.cardNumberComplete = complete;
          if (brand) {
            this.currentCardBrand = brand;
          }
        } else if (elementType === 'cardExpiry') {
          this.cardExpiryComplete = complete;
        } else if (elementType === 'cardCvc') {
          this.cardCvcComplete = complete;
        }

        // When an input field is complete, focus the next one
        if (complete) {
          this.focusNextField(elementType)
        }

        this.checkAndConfirmSetupIntent();
      }
    },
    
    onZipChange() {
      this.$validator.validate('postalCode', this.addressZip).then((valid) => {
        this.postalCodeComplete = valid;
        this.checkAndConfirmSetupIntent();
      });
    },
    
    checkAndConfirmSetupIntent() {
      // Check if all fields are complete
      if (this.cardNumberComplete && this.cardExpiryComplete &&
        this.cardCvcComplete && this.postalCodeComplete &&
        !this.creatingPaymentMethod) {

        this.confirmSetupIntent();
      } else if (!this.creatingPaymentMethod) {
        this.$emit('token-generated', {token: null, funding: null});
      }
    },

    regenerateToken() {
      // Regenerate token from existing card data after 422 error
      if (this.cardNumberComplete && this.cardExpiryComplete &&
        this.cardCvcComplete && this.postalCodeComplete) {
        this.confirmSetupIntent();
      }
    },
    
    async confirmSetupIntent() {
      if (this.creatingPaymentMethod) {
        return;
      }
      
      // Prevent rapid-fire calls
      this.creatingPaymentMethod = true;
      
      // Always create a fresh Setup Intent for each tokenization attempt
      // This ensures we never reuse a confirmed Setup Intent
      this.setupIntentClientSecret = null;
      
      await this.createSetupIntent();
      if (!this.setupIntentClientSecret) {
        // Failed to create setup intent
        this.creatingPaymentMethod = false;
        return;
      }

      this.updateElementsDisabledState(true);
      
      try {
        // Step 1: Create payment method with custom fields
        const {error: pmError, paymentMethod} = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.cardNumber,
          billing_details: {
            address: {
              postal_code: this.addressZip
            }
          }
        });

        if (pmError) {
          this.processingErrorMessage = pmError.message;
          this.notifyError(pmError.message);
          this.creatingPaymentMethod = false;
          this.updateElementsDisabledState(false);
          // Clear setup intent so a new one is created on next attempt
          this.setupIntentClientSecret = null;
          return;
        }

        // Step 2: Confirm Setup Intent with payment method ID
        const {error, setupIntent} = await this.stripe.confirmSetupIntent(this.setupIntentClientSecret, {
          payment_method: paymentMethod.id
        });

        if (error) {
          this.processingErrorMessage = error.message;
          this.notifyError(error.message);
          this.creatingPaymentMethod = false;
          this.updateElementsDisabledState(false);
          // Clear setup intent so a new one is created on next attempt
          this.setupIntentClientSecret = null;
        } else {
          // Use the payment method we created
          this.createStripeToken({
            id: paymentMethod.id,
            card: {
              last4: paymentMethod.card.last4,
              funding: paymentMethod.card.funding
            }
          });
          // Clear setup intent after successful use
          this.setupIntentClientSecret = null;
        }
        
      } catch (error) {
        this.processingErrorMessage = `Error: ${error.message || 'An error occurred while setting up the payment method!'}`;
        this.notifyError("An error occurred while setting up the payment method.");
        this.creatingPaymentMethod = false;
        this.updateElementsDisabledState(false);
        // Clear setup intent so a new one is created on next attempt
        this.setupIntentClientSecret = null;
      }
    },
    
    updateElementsDisabledState(disabled) {
      // Update the disabled state of Stripe elements
      if (this.cardNumber) {
        this.cardNumber.update({ disabled: disabled });
      }
      if (this.cardExpiry) {
        this.cardExpiry.update({ disabled: disabled });
      }
      if (this.cardCvc) {
        this.cardCvc.update({ disabled: disabled });
      }
    },
    
    focusNextField(elementType) {
      const focusOrder = ['cardNumber', 'cardExpiry', 'cardCvc', 'zip'];
      const currentIndex = focusOrder.indexOf(elementType);
      const nextIndex = currentIndex + 1;

      const nextElementKey = focusOrder[nextIndex];
      const nextElement = this[nextElementKey];

      if (elementType === 'cardCvc') {
        if (this.currentCardBrand !== 'amex') {
          this.$refs.addressZip.focus();
        }
      } else {
        if (nextElement) {
          nextElement.focus()
        }
      }
    },
    
    createStripeToken(paymentMethodData) {
      
      // Reuse existing endpoint but pass payment method ID as "token"
      this.$http.post("/api/v1/public/stripe_tokens", {
        token: paymentMethodData.id,
        last4: paymentMethodData.card.last4
      }).then(response => {
        this.creatingPaymentMethod = false;
        this.updateElementsDisabledState(false);
        this.processingErrorMessage = null;
        this.$emit('token-generated', {
          token: response.data.token, 
          funding: paymentMethodData.card.funding
        });
      }, (error) => {
        this.creatingPaymentMethod = false;
        this.updateElementsDisabledState(false);
        this.notifyError("An error occurred while saving the payment method.");
        this.processingErrorMessage = `Error saving payment method: ${error.statusText || 'Unknown error'}`;
      })
    }
  },
  computed: {
    isPartialCanadianZip() {
      return /^[A-Za-z]/.test(this.addressZip);
    },
    isPartialUSZip() {
      return /^[0-9]{1,5}(-[0-9]{1,4})?$/.test(this.addressZip);
    },
    zipLength() {
      if (this.isPartialCanadianZip) {
        return 7
      } else if (this.isPartialUSZip) {
        return 5
      } else {
        return 1000
      }
    },
    zipLabel() {
      if (this.isPartialCanadianZip) {
        return "Zip (CAN)";
      } else if (this.isPartialUSZip) {
        return "Zip (US)";
      } else {
        return "Zip";
      }
    },
    zipMask() {
      if (this.isPartialCanadianZip) {
        return "A#A #A#";
      } else if (this.isPartialUSZip) {
        return "#####";
      } else {
        return "#####";
      }
    }
  }
}
</script>
<style scoped>
.stripe-input {
  display: block;
  margin-top: 5px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.zip-input {
  display: block !important;
  margin-top: 5px !important;
  padding: 10px !important;
  border: 1px solid #ccc !important;
  border-radius: 5px !important;
  font-size: 14px !important;
  box-sizing: border-box !important;
  outline: none !important;
  height: auto !important;
}

.zip-input::placeholder {
  color: #AAB7C4;
}

.zip-input::-webkit-input-placeholder {
  color: #AAB7C4;
}

.zip-input::-moz-placeholder {
  color: #AAB7C4;
  opacity: 1;
}

.zip-input:-ms-input-placeholder {
  color: #AAB7C4;
}

.zip-input:-moz-placeholder {
  color: #AAB7C4;
  opacity: 1;
}

.zip-input:focus {
  border-bottom: 1px solid #ccc !important;
  box-shadow: none !important;
}

.zip-input.invalid {
  border-bottom: 1px solid #ccc !important;
  box-shadow: none !important;
}

.zip-input.valid {
  border-bottom: 1px solid #ccc !important;
  box-shadow: none !important;
}

.disabled-form {
  opacity: 0.7;
  pointer-events: none;
}

.disabled-input {
  background-color: #f5f5f5 !important;
  cursor: not-allowed !important;
}
</style>
